/**
 * <quick-view-modal> (snippets/cart-drawer.liquid): a second top-layer
 * <dialog>, opened over an already-open cart drawer, that shows a single
 * product's buy box fetched server-side from sections/quick-view-card.liquid
 * via the Section Rendering API (`<product_url>?section_id=quick-view-card`).
 * Variant selection, price formatting, and add-to-cart all come from that
 * fetched markup's own machinery (snippets/variant-picker,
 * snippets/buy-buttons, assets/variant-picker.js) - this file only owns the
 * modal shell (open/close, focus trap, Esc, backdrop click, scroll lock)
 * and the lightweight dot-pagination for the fetched image slides.
 *
 * Triggered by assets/recently-viewed.js's "View" button
 * (data-recent-view, data-product-url).
 */
(function () {
  if (!customElements.get('quick-view-modal')) {
    class QuickViewModal extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.content = this.querySelector('[data-quick-view-content]');
        if (!this.dialog || !this.content) return;

        this.triggerEl = null;
        this.loadToken = 0;

        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDialogClose = this.onDialogClose.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);
        this.onCartUpdated = this.onCartUpdated.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        this.dialog.addEventListener('close', this.onDialogClose);
        // Capture phase: only records that an add-to-cart submit started
        // inside the modal content, without interfering with cart.js's own
        // (bubbling) submit listener that actually performs the add.
        this.addEventListener('submit', this.onSubmitCapture, true);
        document.addEventListener('cart:updated', this.onCartUpdated);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        if (this.dialog) {
          this.dialog.removeEventListener('close', this.onDialogClose);
          if (window.ThemeOverlay) window.ThemeOverlay.cancelClose(this.dialog);
        }
        this.removeEventListener('submit', this.onSubmitCapture, true);
        document.removeEventListener('cart:updated', this.onCartUpdated);
      }

      open(productUrl, triggerEl) {
        if (!this.dialog || !productUrl) return;
        this.triggerEl = triggerEl || null;
        this.pendingClose = false;

        this.content.innerHTML = '';
        this.content.classList.add('is-loading');
        // Shared with the cart drawer, the concierge modal and the gallery
        // lightbox - see assets/overlay-motion.js.
        if (window.ThemeOverlay) {
          window.ThemeOverlay.openDialog(this.dialog);
        } else {
          this.dialog.showModal();
          this.dialog.classList.add('is-visible');
        }
        // Stack-aware: quick view opens on top of the cart drawer, which is
        // already holding a lock of its own. See ThemeOverlay.lockScroll.
        if (window.ThemeOverlay) window.ThemeOverlay.lockScroll(this);

        var token = ++this.loadToken;
        var url = new URL(productUrl, window.location.origin);
        url.searchParams.set('section_id', 'quick-view-card');

        fetch(url.toString(), { headers: { Accept: 'text/html' } })
          .then(function (response) {
            return response.ok ? response.text() : null;
          })
          .catch(function () {
            return null;
          })
          .then((html) => {
            if (token !== this.loadToken) return; // a newer open() superseded this one
            this.content.classList.remove('is-loading');
            if (!html) return;

            var doc = new DOMParser().parseFromString(html, 'text/html');
            var card = doc.querySelector('[data-quick-view-card]');
            if (!card) return;

            this.content.innerHTML = card.outerHTML;
            /* Fetched markup never sees DOMContentLoaded or
               shopify:section:load, so without this the quick-view imagery
               shimmers as "loading" for as long as the modal is open. */
            if (window.ThemeReveal) window.ThemeReveal.init(this.content);
            this.setupGallery();

            var heading = this.content.querySelector('[data-quick-view-heading]');
            if (heading && heading.id) this.dialog.setAttribute('aria-labelledby', heading.id);

            this.focusFirst();
          });
      }

      close() {
        if (!this.dialog || !this.dialog.open) return;
        if (window.ThemeOverlay) {
          window.ThemeOverlay.closeDialog(this.dialog);
        } else {
          this.dialog.close();
        }
      }

      onDialogClose() {
        this.dialog.classList.remove('is-visible');
        this.loadToken += 1; // invalidate any in-flight fetch
        if (window.ThemeOverlay) {
          window.ThemeOverlay.unlockScroll(this);
          window.ThemeOverlay.cancelClose(this.dialog);
          window.ThemeOverlay.restoreFocus(this.triggerEl);
        } else if (this.triggerEl && typeof this.triggerEl.focus === 'function') {
          this.triggerEl.focus();
        }
        this.triggerEl = null;
      }

      focusFirst() {
        if (!window.ThemeOverlay) return;
        var target = window.ThemeOverlay.getFocusable(this.content)[0];
        if (target) target.focus();
      }

      setupGallery() {
        var slides = Array.from(this.content.querySelectorAll('[data-quick-view-slide]'));
        var dots = Array.from(this.content.querySelectorAll('[data-quick-view-dot]'));
        if (!slides.length || !dots.length) return;

        dots.forEach((dot, index) => {
          dot.addEventListener('click', () => {
            slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
            dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
          });
        });
      }

      onClick(event) {
        if (event.target.closest('[data-quick-view-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        // A click landing directly on the <dialog> element (not one of its
        // rendered children) is a backdrop click.
        if (event.target === this.dialog) this.close();
      }

      // Shared with the concierge modal and the two <details> overlays in
      // header.js - see ThemeOverlay.trapTab.
      onKeydown(event) {
        if (window.ThemeOverlay) window.ThemeOverlay.trapTab(event, this.dialog);
      }

      // Marks that an add-to-cart form inside the fetched quick-view content
      // was submitted, so the next cart:updated (dispatched by
      // window.CartAPI.add's refresh - see assets/cart.js) knows to close
      // this modal and reveal the drawer's Cart tab underneath.
      onSubmitCapture(event) {
        var form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!/\/cart\/add(\.js)?(\?|$)/.test(form.action)) return;
        if (!form.closest('[data-quick-view-content]')) return;
        this.pendingClose = true;
      }

      onCartUpdated() {
        if (!this.pendingClose) return;
        this.pendingClose = false;
        this.close();
      }
    }

    customElements.define('quick-view-modal', QuickViewModal);
  }
})();
